import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { DollarSign, Trash2, Calendar, User, FileSpreadsheet, Copy, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ExpensesPage = () => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  // Driver entry form link
  const driverFormLink = `${window.location.origin}/driver/expenses`;

  useEffect(() => {
    fetchExpenses();
  }, []);

  const fetchExpenses = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/expenses`);
      setExpenses(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching expenses:', error);
      toast.error('Failed to load expenses');
      setLoading(false);
    }
  };

  const handleDelete = async (expenseId) => {
    if (!window.confirm('Are you sure you want to delete this expense entry?')) {
      return;
    }

    try {
      await axios.delete(`${BACKEND_URL}/api/expenses/${expenseId}`);
      toast.success('Expense deleted successfully');
      fetchExpenses();
    } catch (error) {
      console.error('Error deleting expense:', error);
      toast.error('Failed to delete expense');
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(driverFormLink);
    setCopied(true);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '-';
    return `₹${parseFloat(value).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      day: '2-digit',
      month: 'short', 
      year: 'numeric' 
    });
  };

  const calculateTotalEarnings = (expense) => {
    // For outstation trips, only cash_collected is used
    if (expense.trip_type === 'outstation') {
      return parseFloat(expense.cash_collected || 0);
    }
    // For regular trips, sum all earning sources
    const digital = parseFloat(expense.digital_payment || 0);
    const uber = parseFloat(expense.uber_wallet || 0);
    const ola = parseFloat(expense.ola_earning || 0);
    const rapido = parseFloat(expense.rapido_earning || 0);
    const namma = parseFloat(expense.namma_yatri_earning || 0);
    const cash = parseFloat(expense.cash_collected || 0);
    return digital + uber + ola + rapido + namma + cash;
  };

  const calculateTotalExpenses = (expense) => {
    const cng = parseFloat(expense.cng || 0);
    const petrol = parseFloat(expense.petrol || 0);
    const subscription = parseFloat(expense.subscription || 0);
    const toll = parseFloat(expense.toll_expense || 0);
    const personal = parseFloat(expense.personal_expense || 0);
    return cng + petrol + subscription + toll + personal;
  };

  const calculateNetProfit = (expense) => {
    return calculateTotalEarnings(expense) - calculateTotalExpenses(expense);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto mb-4"></div>
          <p className="text-gray-600">Loading expenses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Expense Tracking</h2>
              <p className="text-gray-600">Daily expense entries and earnings summary</p>
            </div>
          </div>

          {/* Driver Form Link */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <FileSpreadsheet className="w-6 h-6 text-blue-600" />
                <div>
                  <p className="font-semibold text-blue-900">Driver Entry Form Link</p>
                  <p className="text-sm text-blue-700">Share this link with drivers to submit daily expenses</p>
                </div>
              </div>
              <button
                onClick={copyToClipboard}
                className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Link
                  </>
                )}
              </button>
            </div>
            <div className="mt-3 bg-white rounded p-3 border border-blue-200">
              <code className="text-sm text-gray-700 break-all">{driverFormLink}</code>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600 font-medium">Total Entries</p>
                    <p className="text-3xl font-bold text-green-900">{expenses.length}</p>
                  </div>
                  <DollarSign className="w-12 h-12 text-green-600 opacity-50" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Expenses List */}
        {expenses.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <DollarSign className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 text-lg">No expense entries yet</p>
            <p className="text-gray-500 text-sm mt-2">Share the driver form link to start collecting expense data</p>
          </div>
        ) : (
          <div className="space-y-4">
            {expenses.map((expense) => (
              <Card key={expense.id} className="border-2 border-gray-200 hover:shadow-lg transition-shadow">
                <CardHeader className="bg-gradient-to-r from-gray-50 to-white border-b pb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <Calendar className="w-6 h-6 text-gray-600" />
                      <div>
                        <CardTitle className="text-xl font-bold text-gray-900 flex items-center space-x-2">
                          <span>{formatDate(expense.date || expense.start_date)}</span>
                          {expense.trip_type === 'outstation' && (
                            <Badge className="bg-orange-100 text-orange-700 border border-orange-300">
                              🚗 OUTSTATION
                            </Badge>
                          )}
                        </CardTitle>
                        {expense.start_date && expense.end_date && (
                          <p className="text-sm text-gray-600 mt-1">
                            {formatDate(expense.start_date)} → {formatDate(expense.end_date)}
                          </p>
                        )}
                        {expense.driver_name && (
                          <p className="text-sm text-gray-600 mt-1 flex items-center">
                            <User className="w-4 h-4 mr-1" />
                            {expense.driver_name}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Badge className={`text-lg px-4 py-2 ${
                        calculateNetProfit(expense) >= 0 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        Net: {formatCurrency(calculateNetProfit(expense))}
                      </Badge>
                      <button
                        onClick={() => handleDelete(expense.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete entry"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Odometer Readings */}
                    <div className="space-y-3">
                      <h3 className="font-semibold text-gray-900 border-b pb-2">Odometer</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Start Reading:</span>
                          <span className="font-medium">{expense.day_start_reading || '-'} km</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">End Reading:</span>
                          <span className="font-medium">{expense.day_end_reading || '-'} km</span>
                        </div>
                        <div className="flex justify-between pt-2 border-t">
                          <span className="text-gray-900 font-semibold">Total Kms:</span>
                          <span className="font-bold text-blue-600">{expense.total_kms || 0} km</span>
                        </div>
                      </div>
                    </div>

                    {/* Expenses */}
                    <div className="space-y-3">
                      <h3 className="font-semibold text-gray-900 border-b pb-2">Expenses</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">CNG:</span>
                          <span className="font-medium">{formatCurrency(expense.cng)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Petrol:</span>
                          <span className="font-medium">{formatCurrency(expense.petrol)}</span>
                        </div>
                        {expense.trip_type !== 'outstation' && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Subscription:</span>
                            <span className="font-medium">{formatCurrency(expense.subscription)}</span>
                          </div>
                        )}
                        {expense.trip_type === 'outstation' && expense.toll_expense && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Toll Expense:</span>
                            <span className="font-medium">{formatCurrency(expense.toll_expense)}</span>
                          </div>
                        )}
                        {expense.personal_expense && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Personal Expense:</span>
                            <span className="font-medium">{formatCurrency(expense.personal_expense)}</span>
                          </div>
                        )}
                        <div className="flex justify-between pt-2 border-t">
                          <span className="text-gray-900 font-semibold">Total Expenses:</span>
                          <span className="font-bold text-red-600">{formatCurrency(calculateTotalExpenses(expense))}</span>
                        </div>
                      </div>
                    </div>

                    {/* Earnings */}
                    <div className="space-y-3">
                      <h3 className="font-semibold text-gray-900 border-b pb-2">
                        {expense.trip_type === 'outstation' ? 'Total Earning' : 'Earnings'}
                      </h3>
                      <div className="space-y-2 text-sm">
                        {expense.trip_type === 'outstation' ? (
                          // Outstation - show only total collected
                          <div className="flex justify-between pt-2">
                            <span className="text-gray-900 font-semibold">Trip Amount:</span>
                            <span className="font-bold text-green-600">{formatCurrency(expense.cash_collected)}</span>
                          </div>
                        ) : (
                          // Regular trip - show all earning sources
                          <>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Digital Payment:</span>
                              <span className="font-medium">{formatCurrency(expense.digital_payment)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Uber Wallet:</span>
                              <span className="font-medium">{formatCurrency(expense.uber_wallet)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Ola:</span>
                              <span className="font-medium">{formatCurrency(expense.ola_earning)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Rapido:</span>
                              <span className="font-medium">{formatCurrency(expense.rapido_earning)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Cash Collected:</span>
                              <span className="font-medium">{formatCurrency(expense.cash_collected)}</span>
                            </div>
                            <div className="flex justify-between pt-2 border-t">
                              <span className="text-gray-900 font-semibold">Total Earnings:</span>
                              <span className="font-bold text-green-600">{formatCurrency(calculateTotalEarnings(expense))}</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  {expense.description && (
                    <div className="mt-4 pt-4 border-t">
                      <h3 className="font-semibold text-gray-900 mb-2">Notes:</h3>
                      <p className="text-gray-700 text-sm bg-gray-50 rounded p-3">{expense.description}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
